/**
 * Which client a fork belongs to.
 *
 * `GET /plan-template/{id}/full` does not say. `PlanTemplateFullResponse` carries the live execution
 * mode, the first open cycle and the consumed prefix — everything about the ASSIGNMENT's progress —
 * and no customer at all, which is why `responseToDraft` sets `clientName: null` and notes that the
 * screen merges it in. This is that merge, and it exists because PR-D needs the id rather than the
 * name: the e1RM context line reads `GET /customer/{id}/exercise/{exerciseId}/records`.
 *
 * ── Resolved from the trainer's own assignment list, not from a new endpoint ─────────────────
 * `GET /plan-assignment` returns the trainer's assignments as summaries carrying both `customerId`
 * and `planTemplateId`, so the fork's client is a lookup in a list the app is entitled to and would
 * fetch for the calendar anyway. CLAUDE.md's rule holds: "reusing an existing endpoint is always
 * preferred over adding one", and nothing here asks the backend for anything new.
 *
 * The list is small — one row per assignment a trainer has ever made — and it changes only when they
 * assign or end a plan, so it caches for the session rather than per plan. One entry serves every
 * exercise's e1RM read on every plan the trainer opens.
 *
 * ── `null` is a normal answer, and one of its two causes is a known blind spot ───────────────
 * A library template has no assignment at all, which is the ordinary case and the reason `enabled`
 * is the caller's — such a plan makes no request.
 *
 * The second cause is narrower than an earlier revision of this note claimed. It said "a fork whose
 * assignment was detached", which describes nothing the backend does. What the backend actually does
 * is FILTER: `PlanAssignmentProcessor` builds the trainer list from Active, Paused and Completed
 * assignments and **excludes `Cancelled`** — the status R6's "end a plan early" stamps. So a fork
 * whose plan was ended early is still a real fork, opened on a real client, and this lookup answers
 * `null` for it. The trainer edits that plan with the RealMax e1RM line silently absent, showing the
 * bare formula as though there were no client, and nothing on screen says why.
 *
 * Not worked around here: the alternative is a second read per plan against an endpoint that would
 * have to expose cancelled assignments, and whether an ended plan should still resolve its client's
 * max is a product question rather than a mapping one. Either way the line is absent rather than
 * wrong — never "0 kg", never a spinner that never ends — which is the property that matters most.
 */

import { queryOptions } from '@tanstack/react-query';

import { getPlanAssignment } from '~/shared/api/generated/endpoints/plan-assignment/plan-assignment';

import {
  planTemplateAssignments,
  type PlanTemplateAssignments,
} from '../model/planTemplateAssignments';

export const planAssignmentListQueryKey = ['plan-assignment', 'list'] as const;

/** The read itself. One key, one request; every options object below is a `select` over it. */
function assignmentRead() {
  return {
    queryKey: planAssignmentListQueryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) => getPlanAssignment(undefined, signal),
    // The list changes only when the trainer assigns or ends a plan, neither of which happens while
    // a rule bar is open. Five minutes keeps a builder session on one request.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  };
}

/**
 * What this list says about one TEMPLATE: the assignment it is opened as, and how many live plans
 * were forked from it. `planTemplateAssignments` is the whole of the rule; this is the read.
 *
 * A second projection of the same cache entry rather than a second request — the same arrangement
 * the plans list uses for its own two selects, for the same reason: a key of its own would put a
 * second copy of the trainer's whole assignment list in the cache and the two would drift apart the
 * moment one was invalidated.
 */
export function planTemplateAssignmentsQueryOptions(planTemplateId: number) {
  return queryOptions({
    ...assignmentRead(),
    // The clock is read at SELECT time rather than captured when the options are built: an options
    // object that baked a timestamp in would keep answering yesterday's "ahead of today" for as long
    // as the screen stayed open, and this select re-runs whenever the list does.
    select: (assignments): PlanTemplateAssignments =>
      planTemplateAssignments(assignments, planTemplateId, Date.now()),
  });
}

/**
 * The customer this template is assigned to, or `null`.
 *
 * The FIRST match wins. A template can in principle appear in the list twice — a plan assigned, ended
 * and re-assigned to the same client — and every such row names the same customer, so the first is as
 * good as any. Two different customers sharing one fork template is not a shape the backend produces:
 * an assignment forks its source, so each client gets a template of their own.
 */
export function planForkCustomerQueryOptions(planTemplateId: number) {
  return queryOptions({
    ...assignmentRead(),
    select: (assignments) =>
      assignments.find((assignment) => assignment.planTemplateId === planTemplateId)?.customerId ??
      null,
  });
}
